import ky, { type KyInstance, type BeforeRequestHook, type AfterResponseHook, type BeforeErrorHook } from "ky";
import { API_URL } from "@/shared/lib/constants";
import { toUserFacingError } from "@/shared/lib/errors";
import { createLogger } from "@/shared/lib/logger";

const log = createLogger("API");

// ============================================================
// Auth token accessors (set by auth store, read by interceptors)
// ============================================================

let _getAccessToken: () => string | null = () => null;
let _getRefreshToken: () => string | null = () => null;
let _onTokensRefreshed: (access: string, refresh: string) => void = () => {};
let _onAuthFailed: () => void = () => {};

export function setAuthBridge(bridge: {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  onTokensRefreshed: (access: string, refresh: string) => void;
  onAuthFailed: () => void;
}) {
  _getAccessToken = bridge.getAccessToken;
  _getRefreshToken = bridge.getRefreshToken;
  _onTokensRefreshed = bridge.onTokensRefreshed;
  _onAuthFailed = bridge.onAuthFailed;
  log.debug("Auth bridge wired");
}

// ============================================================
// 401 Refresh Queue — one refresh at a time
// ============================================================

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}> = [];

function processQueue(token: string | null, error: Error | null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error ?? new Error("Refresh failed"));
  });
  refreshQueue = [];
}

export async function refreshOrQueue(): Promise<string> {
  if (isRefreshing) {
    log.debug("Refresh in progress, queuing");
    return new Promise<string>((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  log.debug("Starting token refresh");
  try {
    const newToken = await doRefresh();
    processQueue(newToken, null);
    return newToken;
  } catch (err) {
    processQueue(null, err as Error);
    throw err;
  } finally {
    isRefreshing = false;
  }
}

async function doRefresh(): Promise<string> {
  const refreshToken = _getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token");

  const res = await fetch(`${API_URL}/api/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!res.ok) {
    log.error("Token refresh failed", { status: res.status });
    throw new Error(`Refresh failed: ${res.status}`);
  }

  const json = await res.json();
  const payload = json.data ?? json;
  const newAccess: string = payload.auth?.token;
  const newRefresh: string = payload.auth?.refresh_token;

  if (!newAccess) throw new Error("No access token in refresh response");

  _onTokensRefreshed(newAccess, newRefresh);
  log.debug("Token refresh successful");
  return newAccess;
}

// ============================================================
// Interceptors
// ============================================================

const injectToken: BeforeRequestHook = (request) => {
  const token = _getAccessToken();
  if (token) {
    request.headers.set("Authorization", `Bearer ${token}`);
  }
};

const handle401: AfterResponseHook = async (request, _options, response) => {
  if (response.status !== 401) return response;

  if (request.url.includes("/api/auth/refresh")) {
    _onAuthFailed();
    return response;
  }

  log.debug("401 received, attempting refresh", { url: request.url });

  try {
    const newToken = await refreshOrQueue();
    request.headers.set("Authorization", `Bearer ${newToken}`);
    return fetch(request);
  } catch {
    _onAuthFailed();
    return response;
  }
};

const mapErrorToUserFacing: BeforeErrorHook = async (error) => {
  const status = error.response?.status;
  if (status) {
    let serverMessage: string | undefined;
    try {
      const body = (await error.response.json()) as Record<string, unknown>;
      if (body && typeof body.message === "string" && body.message) {
        serverMessage = body.message;
      }
    } catch {
      // response body not JSON
    }
    throw toUserFacingError(status, serverMessage);
  }
  return error;
};

// ============================================================
// Configured ky instance → Kong Gateway
// ============================================================

export const api: KyInstance = ky.create({
  prefixUrl: API_URL,
  timeout: 30_000,
  retry: {
    limit: 2,
    methods: ["get"],
    statusCodes: [408, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [injectToken],
    afterResponse: [handle401],
    beforeError: [mapErrorToUserFacing],
  },
});

/**
 * Raw fetch for SSE and special cases (no ky wrapping).
 * Handles 401 with token refresh + single retry.
 */
export async function rawFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = _getAccessToken();
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  log.debug("rawFetch", { url });
  const res = await fetch(url, { ...init, headers });

  if (res.status !== 401 || url.includes("refresh")) return res;

  log.debug("rawFetch 401, refreshing token");
  try {
    const newToken = await refreshOrQueue();
    const retryHeaders = new Headers(init?.headers);
    retryHeaders.set("Authorization", `Bearer ${newToken}`);
    return fetch(url, { ...init, headers: retryHeaders });
  } catch {
    _onAuthFailed();
    return res;
  }
}

/**
 * Expose current access token for non-ky transports (e.g. XHR upload).
 */
export function getCurrentToken(): string | null {
  return _getAccessToken();
}

export { STORAGE_KEYS } from "@/shared/lib/constants";
